import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import { companiesService } from '@/services/CompaniesService'
import { loadPlatformFeatures } from '@/services/PlatformFeaturesService'
import { canUseRole, getActiveRoleName, permissionDeniedMessage } from '@/utils/permissions'

// Guard para rutas que requieren autenticación
export function requireAuth(to, from, next) {
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
    return
  }

  const requiredPermission = to.meta?.permission
  if (requiredPermission && !canUseRole(getActiveRoleName(authStore), requiredPermission)) {
    useAppStore().addError(permissionDeniedMessage(requiredPermission))
    next(from?.name ? false : '/dashboard')
    return
  }

  next()
}

function passwordResetTokenFromRoute(to) {
  const paramToken = to.params.token
  if (typeof paramToken === 'string' && paramToken.length > 0) return paramToken
  const q = to.query.token
  if (typeof q === 'string' && q.length > 0) return q
  if (Array.isArray(q) && q[0]) return q[0]
  return ''
}

// Guard para rutas de invitados (login, register)
export function requireGuest(to, from, next) {
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    next()
    return
  }

  // Enlace del correo: suele abrirse con sesión aún activa; debe poder completar el flujo
  if (to.path.startsWith('/reset-password') && passwordResetTokenFromRoute(to)) {
    next()
    return
  }
  if (to.path.startsWith('/accept-invitation') && passwordResetTokenFromRoute(to)) {
    next()
    return
  }

  next('/dashboard')
}

// Guard para propietarios del negocio
export function requireOwner(to, from, next) {
  const authStore = useAuthStore()
  
  if (authStore.isAuthenticated && authStore.isBusinessOwner) {
    next()
  } else if (authStore.isAuthenticated) {
    // Está autenticado pero no es propietario
    next('/dashboard')
  } else {
    // No está autenticado
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  }
}

// Guard para empleados (owner + employee)
export function requireEmployee(to, from, next) {
  const authStore = useAuthStore()
  
  if (authStore.isAuthenticated && (authStore.isBusinessOwner || authStore.isEmployee)) {
    next()
  } else if (authStore.isAuthenticated) {
    // Está autenticado pero no tiene permisos
    next('/dashboard')
  } else {
    // No está autenticado
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  }
}

// PUT/PATCH/POST/DELETE quedan bloqueados en modo suplantación (ver api.js isImpersonationActive),
// incluido POST /users/onboarding/complete: mandar a un admin impersonando por el tour lo deja sin
// forma de terminarlo ni de salir (el tour es del dueño impersonado, no de la sesión del admin).
const SUBSCRIPTION_SETTINGS_PATH = '/dashboard/settings/subscription'

// Redirige a /onboarding si el usuario autenticado no lo completó, pero solo dentro del área
// protegida (dashboard) — misma condición que App.vue usa para decidir el layout, así no se
// interponen rutas públicas, de auth, de booking, ni loops entre login→dashboard→onboarding.
export function requireOnboarding(to, from, next) {
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated || to.path === '/onboarding' || authStore.isImpersonating) {
    next()
    return
  }

  // /subscription/success y /subscription/cancel redirigen acá mismo (ver main.js): Vue Router
  // resuelve el redirect antes de este guard, así que to.path ya llega como el destino final.
  const isProtectedArea = to.path.startsWith('/dashboard') && to.path !== SUBSCRIPTION_SETTINGS_PATH
  if (!isProtectedArea || authStore.user?.onboarding_completed_at) {
    next()
    return
  }

  next({ path: '/onboarding', query: { redirect: to.fullPath } })
}

// Función para inicializar la autenticación antes de navegar
export async function initAuth() {
  const authStore = useAuthStore()
  
  try {
    const isAuthenticated = await authStore.initializeAuth()
    if (isAuthenticated) {
      await loadPlatformFeatures()
      await loadInitialCompanies(authStore)
    }
  } catch (error) {
    console.error('Error inicializando autenticación:', error)
  }
}

async function loadInitialCompanies(authStore) {
  try {
    const { active } = await companiesService.loadAndSetInitialCompany()
    if (active) {
      authStore.updateBusiness({
        name: active.name,
        id: active.id,
        tenant_id: active.tenant_id,
        email: active.email || authStore.business?.email,
        phone: active.phone || authStore.business?.phone,
        logo: active.logo || authStore.business?.logo
      })
    }
  } catch (error) {
    console.error('Error cargando empresas iniciales:', error)
  }
}

